import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'components/common/data-table/data-table-component';
import { useTableScroll } from 'components/hooks/useTableScroll';
import { useTableColumns } from 'components/hooks/useTableColumn';
import { trialBalanceSnapShotView } from 'constants/util/side-by-side-utils';
import { getContentPanelHeight } from 'constants/feature/statement-content-constants';

const INCOME_STATEMENT_BLOCK = 'income-statement-view';
const DATA_KEY_NAME = 'IncomeStatement';

function IncomeStatementView({
  trialBalanceData,
  scrollToId,
  mappedElements,
  onAmountClick,
  sideBySideElementMap,
  cellHighlight,
  tbClickedElement,
  highlightMappedElements,
}) {
  const selectedStatement = useSelector(
    (state) => state?.data?.selectedStatement,
  );
  const {
    sideBySideViewMode,
    trialBalance: { tbWorkFlowStatus },
  } = useSelector((state) => state?.ui?.sideBySideView);
  const { scrollConfig, tableConfig } = trialBalanceSnapShotView;
  const [incomeStatementData] = trialBalanceData?.financialStatement.filter(
    (data) => data.name === DATA_KEY_NAME,
  );
  const tableData = useMemo(() => incomeStatementData?.category, [incomeStatementData?.category]);
  const currency = trialBalanceData?.currency;
  const { defaultScaling, numberFormatId } = selectedStatement?.statement;

  // Get headers with currency formatting and click handler applied to amount column
  const headers = useMemo(
    () =>
      trialBalanceSnapShotView.getHeaders({
        currency,
        mappedElements,
        defaultScaling,
        numberFormatId,
        onAmountClick,
        sideBySideElementMap,
        cellHighlight,
        tbClickedElement,
        highlightMappedElements,
      }),
    [
      mappedElements,
      onAmountClick,
      sideBySideElementMap,
      cellHighlight,
      tbClickedElement,
      defaultScaling,
      numberFormatId,
      highlightMappedElements,
    ],
  );

  const {
    scrollToRowId,
    tableRef,
    highlightedCellInfo,
    handleScrollToRowByDataId,
  } = useTableScroll(scrollConfig);

  const tableColumns = useTableColumns(headers, {
    showExpander: true,
  });

  // Scroll to row when scrollToId prop changes
  useEffect(() => {
    if (scrollToId) {
      handleScrollToRowByDataId(scrollToId);
    }
  }, [scrollToId]);

  const panelHeight = useMemo(() => {
    const height = getContentPanelHeight({
      sideBySideViewMode,
      isSourceStatementLoaded: true,
      tbWorkFlowStatus,
      hasTabGroupHeader: true,
    });
    return height;
  }, [sideBySideViewMode, tbWorkFlowStatus]);

  return (
    <div
      className={INCOME_STATEMENT_BLOCK}
      style={{ height: `calc(100vh - ${panelHeight}px - var(--opm-banner-height, 0px))` }}
    >
      {tableData ? (
        <Table
          columns={tableColumns}
          data={tableData}
          scrollToRowId={scrollToRowId}
          highlightedCellInfo={highlightedCellInfo}
          onTableReady={(table) => {
            tableRef.current = table;
          }}
          config={tableConfig}
        />
      ) : (
        // This will be updated once the requirements are clear
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            alignContent: 'center',
            height: '100%',
            fontSize: '30px',
            color: 'grey',
          }}
        >
          No data available
        </div>
      )}
    </div>
  );
}

export default IncomeStatementView;
